"use client";

import { useState } from "react";
import SelectionCard, { ChoiceRow } from "./SelectionCard";
import type { TaskCategory } from "@/lib/app-types";

/**
 * Display label → backend outputFormat mapping.
 * Backend only accepts 'html' | 'tsx' | 'md'. Unsupported formats
 * are mapped to undefined (agent decides) at the API layer.
 */
interface FormatOption {
  /** Display label shown in the UI */
  displayLabel: string;
  /** Internal key used for stable React key */
  key: string;
  /** What we send to planTask / confirmTask. undefined = let agent decide */
  apiFormat: "html" | "tsx" | "md" | undefined;
  /** Muted timing/type hint shown before the label */
  prefix?: string;
}

// ── Category synonym sets ─────────────────────────────────────────
// Backend plan.analysis.categories[0] may return broader strings than
// the 5-value TaskCategory union; these sets handle known synonyms.

const WRITING_CATS = new Set([
  "writing", "marketing", "content", "copywriting", "email",
  "blog", "copy", "subject_lines", "email_marketing", "creative",
  "social", "social_media", "pr", "communications",
]);

const CODE_CATS = new Set([
  "code", "development", "design", "engineering", "frontend",
  "coding", "programming", "dev", "web", "software", "react", "html",
  "backend", "fullstack", "mobile", "app",
]);

const DATA_CATS = new Set([
  "data", "research", "analytics", "analysis", "reporting",
  "data_analysis", "spreadsheet", "csv", "business_intelligence",
  "finance", "accounting",
]);

// ── Category → format options map ────────────────────────────────

function getFormatsForCategory(category: TaskCategory | string | undefined | null): FormatOption[] {
  // Normalise to lowercase for consistent set lookup.
  const cat = (category ?? "general").toLowerCase();

  if (WRITING_CATS.has(cat)) {
    return [
      { key: "md", displayLabel: "Markdown", apiFormat: "md", prefix: ".md" },
      { key: "docx", displayLabel: "DOCX", apiFormat: undefined, prefix: "Word" },
      { key: "txt", displayLabel: "Plain Text", apiFormat: undefined, prefix: ".txt" },
      { key: "skip", displayLabel: "Skip — let agent decide", apiFormat: undefined },
    ];
  }

  if (CODE_CATS.has(cat)) {
    return [
      { key: "html", displayLabel: "HTML", apiFormat: "html", prefix: ".html" },
      { key: "tsx", displayLabel: "React / TSX", apiFormat: "tsx", prefix: ".tsx" },
      { key: "vue", displayLabel: "Vue", apiFormat: undefined, prefix: ".vue" },
      { key: "skip", displayLabel: "Skip — let agent decide", apiFormat: undefined },
    ];
  }

  if (DATA_CATS.has(cat)) {
    return [
      { key: "md", displayLabel: "Markdown", apiFormat: "md", prefix: ".md" },
      { key: "csv", displayLabel: "CSV", apiFormat: undefined, prefix: ".csv" },
      { key: "pdf", displayLabel: "PDF", apiFormat: undefined, prefix: ".pdf" },
      { key: "skip", displayLabel: "Skip — let agent decide", apiFormat: undefined },
    ];
  }

  // General / other
  return [
    { key: "md", displayLabel: "Markdown", apiFormat: "md", prefix: ".md" },
    { key: "txt", displayLabel: "Plain Text", apiFormat: undefined, prefix: ".txt" },
    { key: "skip", displayLabel: "Skip — let agent decide", apiFormat: undefined },
  ];
}

interface FormatSelectorProps {
  /** The task category from analysis.categories[0]. Drives option list.
   *  Accepts raw backend strings beyond the TaskCategory union. */
  category?: TaskCategory | string | null;
  /** apiFormat drives the agent prompt; downloadKey drives client-side
   *  download packaging (md/txt/docx/csv/…) so the user gets the file format
   *  they picked even when the backend doesn't model it. */
  onSelect: (format: "html" | "tsx" | "md" | null, downloadKey: string | null) => void;
}

export default function FormatSelector({ category, onSelect }: FormatSelectorProps) {
  const formats = getFormatsForCategory(category);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const selectedOption = formats.find((f) => f.key === selectedKey) ?? null;
  const canConfirm = selectedKey !== null;

  const handleConfirm = () => {
    if (!canConfirm || !selectedOption) return;
    // apiFormat (html|tsx|md) drives the agent; downloadKey carries the user's
    // actual choice (e.g. docx, txt) for client-side download packaging.
    const downloadKey = selectedOption.key === "skip" ? null : selectedOption.key;
    onSelect(selectedOption.apiFormat ?? null, downloadKey);
  };

  const handleSkip = () => {
    onSelect(null, null);
  };

  return (
    <SelectionCard
      label="OUTPUT FORMAT"
      title="What format should the result be in?"
      onConfirm={handleConfirm}
      confirmDisabled={!canConfirm}
      onSkip={handleSkip}
      skipLabel="SKIP — LET AGENT DECIDE"
    >
      {formats.map((fmt) => (
        <ChoiceRow
          key={fmt.key}
          label={fmt.displayLabel}
          prefix={fmt.key !== "skip" ? fmt.prefix : undefined}
          selected={selectedKey === fmt.key}
          onClick={() => setSelectedKey(fmt.key)}
        />
      ))}
    </SelectionCard>
  );
}
